import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import classNames from 'classnames'
import { Loader2 } from 'lucide-react'

import { FOCUS_RING } from 'components/Modals/CreateTournamentModal/ui'
import { isCountryLabel, regionLabelName, regionLabelShort } from 'constants/geo/regionLabels'

/* Shared building blocks for Region Teams screens. Theme tokens only — same
   language as the Create tournament modal (CreateTournamentModal/ui.tsx). */

export {
  Banner,
  ChipGroup,
  FieldError,
  FieldLabel,
  FOCUS_RING,
  Help,
  inputClass,
  NumberStepper,
  SwitchRow,
} from 'components/Modals/CreateTournamentModal/ui'

export const TEXT = 'text-light-typo-primary dark:text-dark-typo-primary'
export const MUTED = 'text-light-typo-gray dark:text-dark-typo-gray'
export const BORDER = 'border-light-border-primary dark:border-dark-border-primary'
export const SURFACE = 'bg-light-card dark:bg-dark-card'
export const SURFACE_2 = 'bg-light-bg-secondary dark:bg-dark-bg'

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  loading?: boolean
  icon?: ReactNode
}

/** Plain button (keeps keyboard focus, unlike the atomic Button which blurs itself). */
export const Btn = forwardRef<HTMLButtonElement, BtnProps>(
  ({ tone = 'secondary', size = 'sm', loading, icon, className, children, disabled, type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classNames(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors whitespace-nowrap',
        'disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-9 px-3 text-sm' : 'h-11 px-4 text-[15px]',
        tone === 'primary' &&
          'bg-gradient-to-r from-btn-fancy-primary-start to-btn-fancy-primary-end text-black hover:from-btn-fancy-primary-start-hover hover:to-btn-fancy-primary-end-hover',
        tone === 'secondary' &&
          classNames('border', BORDER, TEXT, 'bg-transparent hover:border-dark-gray-primary hover:bg-white/[0.03]'),
        tone === 'ghost' && classNames(TEXT, 'hover:bg-light-gray-light dark:hover:bg-dark-bg-secondary'),
        tone === 'danger' && 'border border-red/50 text-red hover:bg-red/10',
        FOCUS_RING,
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={15} className="animate-spin motion-reduce:animate-none" aria-hidden /> : icon}
      {children}
    </button>
  ),
)
Btn.displayName = 'Btn'

/** Region label chip: "OH" (title "Ohio"), "Germany" for "C-DE". */
export const RegionChip = ({ label, className, size = 'sm' }: { label: string; className?: string; size?: 'xs' | 'sm' }) => (
  <span
    title={regionLabelName(label)}
    className={classNames(
      'inline-flex items-center rounded-md border font-semibold tabular-nums',
      size === 'xs' ? 'px-1.5 py-0 text-[10px] leading-4' : 'px-1.5 py-0.5 text-[11px] leading-4',
      isCountryLabel(label)
        ? 'border-cyan/40 bg-cyan/10 text-cyan'
        : 'border-btn-fancy-primary-end/40 bg-btn-fancy-primary-end/10 text-green',
      className,
    )}
  >
    <span className="sr-only">{regionLabelName(label)}</span>
    <span aria-hidden>{regionLabelShort(label)}</span>
  </span>
)

export const Card = ({ className, children, as: As = 'section', ...rest }: { className?: string; children: ReactNode; as?: any; [k: string]: any }) => (
  <As className={classNames('rounded-xl border', BORDER, SURFACE, className)} {...rest}>
    {children}
  </As>
)

export const SectionTitle = ({ children, right, id }: { children: ReactNode; right?: ReactNode; id?: string }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <h3 id={id} className={classNames('font-display text-lg font-bold leading-tight', TEXT)}>
      {children}
    </h3>
    {right}
  </div>
)

/** Small stat tile (number + label) used in summaries. */
export const Stat = ({ value, label, tone }: { value: ReactNode; label: string; tone?: 'warn' }) => (
  <div className={classNames('rounded-lg px-3 py-2', SURFACE_2)}>
    <div className={classNames('font-display text-2xl font-bold tabular-nums leading-tight', tone === 'warn' ? 'text-yellow' : TEXT)}>{value}</div>
    <div className={classNames('text-[11px]', MUTED)}>{label}</div>
  </div>
)
